import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '../../components/DataTable';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { useUsers } from '../../hooks/useUsers';
import { formatDate, formatDateTime } from '../../lib/format';
import type { AdminUser } from '../../types/domain';

export function UserList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const perPage = 50;
  const { data, isLoading } = useUsers(page, perPage);

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const columns = [
    {
      header: 'Email',
      accessor: (row: AdminUser) => <span className="font-medium">{row.email}</span>,
    },
    {
      header: 'Creado',
      accessor: (row: AdminUser) => formatDate(row.created_at),
    },
    {
      header: 'Último acceso',
      accessor: (row: AdminUser) => formatDateTime(row.last_sign_in_at),
    },
    {
      header: 'Admin',
      accessor: (row: AdminUser) =>
        row.app_metadata.is_admin ? <Badge variant="default">Admin</Badge> : null,
    },
    {
      header: 'Acciones',
      accessor: (row: AdminUser) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="outline" onClick={() => navigate(`/users/${row.id}`)}>
            Ver
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Usuarios</h1>
        {total > 0 && <span className="text-sm text-muted-foreground">{total} usuarios</span>}
      </div>

      <DataTable
        columns={columns}
        data={users}
        isLoading={isLoading}
        emptyMessage="No hay usuarios."
        getRowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/users/${row.id}`)}
      />

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-muted-foreground">
            Página {page} de {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft size={14} />
              Anterior
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Siguiente
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
